import React, { useState } from 'react';
import { Plus, Edit, Trash2, Search, Users, Home, Calendar, Filter, MapPin, Building, Zap } from 'lucide-react';
import { useCrud, useModal, useForm } from '../hooks';
import { alocacaoService, caravanaService, quartoService, eventoService, localService, igrejaService } from '../services/api';

const Alocacoes = () => {
  const { data: alocacoes, loading, error, create, update, remove, refresh } = useCrud(alocacaoService, 'alocações');
  const { data: caravanas } = useCrud(caravanaService, 'caravanas');
  const { data: quartos } = useCrud(quartoService, 'quartos');
  const { data: eventos } = useCrud(eventoService, 'eventos');
  const { data: locais } = useCrud(localService, 'locais');
  const { data: igrejas } = useCrud(igrejaService, 'igrejas');
  const modal = useModal();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEvento, setSelectedEvento] = useState('');
  const [selectedQuarto, setSelectedQuarto] = useState('');

  const validationRules = {
    quarto_id: { required: true, label: 'Quarto' },
    caravana_id: { required: true, label: 'Caravana' },
    evento_id: { required: true, label: 'Evento' },
    quantidade: { 
      required: true, 
      label: 'Quantidade',
      custom: (value) => {
        const num = parseInt(value);
        if (isNaN(num) || num <= 0) {
          return 'Quantidade deve ser um número maior que 0';
        }
        return null;
      }
    }
  };

  const form = useForm(
    { quarto_id: '', caravana_id: '', evento_id: '', quantidade: 1, observacoes: '' },
    validationRules
  );

  const filteredAlocacoes = alocacoes.filter(alocacao => {
    const matchesSearch = 
      alocacao.Caravana?.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      alocacao.Quarto?.numero?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      alocacao.Caravana?.Igreja?.nome?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesEvento = selectedEvento === '' || alocacao.evento_id?.toString() === selectedEvento;
    const matchesQuarto = selectedQuarto === '' || alocacao.quarto_id?.toString() === selectedQuarto;
    
    return matchesSearch && matchesEvento && matchesQuarto;
  });

  const handleCreate = () => {
    form.reset();
    modal.open({ mode: 'create', title: 'Nova Alocação' });
  };

  const handleEdit = (alocacao) => {
    form.reset({
      quarto_id: alocacao.quarto_id || '',
      caravana_id: alocacao.caravana_id || '',
      evento_id: alocacao.evento_id || '',
      quantidade: alocacao.quantidade || 1,
      observacoes: alocacao.observacoes || ''
    });
    modal.open({ mode: 'edit', title: 'Editar Alocação', id: alocacao.id });
  };

  const handleDelete = async (alocacao) => {
    const nomeCaravana = alocacao.Caravana?.nome || 'Caravana';
    const numeroQuarto = alocacao.Quarto?.numero || 'Quarto';
    if (window.confirm(`Deseja realmente excluir a alocação da "${nomeCaravana}" no "${numeroQuarto}"?`)) {
      const result = await remove(alocacao.id);
      if (result.success) {
        // Success handled by the hook
      }
    }
  };

  const handleSubmit = async (values) => {
    const data = {
      ...values,
      quantidade: parseInt(values.quantidade)
    };

    const result = modal.data.mode === 'create' 
      ? await create(data)
      : await update(modal.data.id, data);

    if (result.success) {
      modal.close();
      form.reset();
    }
  };

  const getQuartoInfo = (quartoId) => {
    const quarto = quartos.find(q => q.id === quartoId);
    if (!quarto) return { nome: 'N/A', local: 'N/A', capacidade: 0 };
    
    const local = locais.find(l => l.id === quarto.local_id);
    return {
      nome: quarto.numero || quarto.nome || 'N/A',
      local: local?.nome || 'N/A',
      capacidade: quarto.capacidade || 0,
      genero: quarto.genero || 'N/A'
    };
  };

  const getCaravanaInfo = (caravanaId) => {
    const caravana = caravanas.find(c => c.id === caravanaId);
    if (!caravana) return { nome: 'N/A', igreja: 'N/A', total: 0 };
    
    const igreja = igrejas.find(i => i.id === caravana.igreja_id);
    return {
      nome: caravana.nome || 'N/A',
      igreja: igreja?.nome || caravana.Igreja?.nome || 'N/A',
      total: (caravana.qtd_homens || 0) + (caravana.qtd_mulheres || 0)
    };
  };

  const getEventoNome = (eventoId) => {
    const evento = eventos.find(e => e.id === eventoId);
    return evento?.nome || 'N/A';
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Alocações</h1>
        <div className="flex gap-3">
          <button 
            onClick={() => window.location.href = '/alocacao-dinamica'} 
            className="btn btn-secondary"
          >
            <Zap size={20} />
            Alocação Dinâmica
          </button>
          <button onClick={handleCreate} className="btn btn-primary">
            <Plus size={20} />
            Nova Alocação
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="card mb-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar alocações..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input pl-10"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <select
              value={selectedEvento}
              onChange={(e) => setSelectedEvento(e.target.value)}
              className="form-select pl-10 min-w-48"
            >
              <option value="">Todos os eventos</option>
              {eventos.map((evento) => (
                <option key={evento.id} value={evento.id}>
                  {evento.nome}
                </option>
              ))}
            </select>
          </div>
          <div className="relative">
            <Home className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <select
              value={selectedQuarto}
              onChange={(e) => setSelectedQuarto(e.target.value)}
              className="form-select pl-10 min-w-48"
            >
              <option value="">Todos os quartos</option>
              {quartos.map((quarto) => (
                <option key={quarto.id} value={quarto.id}>
                  {quarto.numero || quarto.nome} - {locais.find(l => l.id === quarto.local_id)?.nome || 'N/A'}
                </option>
              ))}
            </select>
          </div>
          <div className="text-sm text-gray-500">
            {filteredAlocacoes.length} alocação(ões) encontrada(s)
          </div>
        </div>
        {(searchTerm || selectedEvento || selectedQuarto) && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">Filtros ativos:</span>
            {searchTerm && (
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                Busca: "{searchTerm}"
              </span>
            )}
            {selectedEvento && (
              <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">
                Evento: {eventos.find(e => e.id.toString() === selectedEvento)?.nome}
              </span>
            )}
            {selectedQuarto && (
              <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs">
                Quarto: {quartos.find(q => q.id.toString() === selectedQuarto)?.numero}
              </span>
            )}
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedEvento('');
                setSelectedQuarto('');
              }}
              className="text-gray-400 hover:text-gray-600 ml-2"
            >
              Limpar filtros
            </button>
          </div>
        )}
      </div>

      <div className="card">
        {filteredAlocacoes.length === 0 ? (
          <div className="empty-state">
            <Users className="empty-state-icon" />
            <h3 className="text-lg font-medium mb-2">Nenhuma alocação encontrada</h3>
            <p className="mb-4">
              {searchTerm || selectedEvento || selectedQuarto ? 'Tente ajustar os filtros de busca.' : 'Comece criando uma nova alocação.'}
            </p>
            {!searchTerm && !selectedEvento && !selectedQuarto && (
              <button onClick={handleCreate} className="btn btn-primary">
                <Plus size={20} />
                Nova Alocação
              </button>
            )}
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Quarto</th>
                  <th>Caravana</th>
                  <th>Evento</th>
                  <th>Quantidade</th>
                  <th>Tipo Alocação</th>
                  <th>Observações</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredAlocacoes.map((alocacao) => {
                  const quartoInfo = getQuartoInfo(alocacao.quarto_id);
                  const caravanaInfo = getCaravanaInfo(alocacao.caravana_id);
                  
                  // Normalizar gênero para display
                  const genero = quartoInfo.genero?.toLowerCase();
                  const tipoAlocacao = (genero === 'masculino' || genero === 'homem') ? 'Homens' : 
                                      (genero === 'feminino' || genero === 'mulher') ? 'Mulheres' : 'Misto';
                  
                  return (
                    <tr key={alocacao.id}>
                      <td>
                        <div>
                          <div className="font-medium flex items-center gap-1">
                            <Home size={14} className="text-gray-400" />
                            {quartoInfo.nome}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center gap-1">
                            <MapPin size={12} />
                            {quartoInfo.local} • Cap: {quartoInfo.capacidade} • {quartoInfo.genero}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div className="font-medium">{caravanaInfo.nome}</div>
                          <div className="text-sm text-gray-500 flex items-center gap-1">
                            <Building size={12} />
                            {caravanaInfo.igreja}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          <Calendar size={14} className="text-gray-400" />
                          <span className="text-sm">{getEventoNome(alocacao.evento_id)}</span>
                        </div>
                      </td>
                      <td className="text-center font-medium">{alocacao.quantidade || 0}</td>
                      <td className="text-center">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                          ${tipoAlocacao === 'Homens' ? 'bg-blue-100 text-blue-800' : 
                            tipoAlocacao === 'Mulheres' ? 'bg-pink-100 text-pink-800' : 
                            'bg-purple-100 text-purple-800'}`}>
                          {tipoAlocacao}
                        </span>
                      </td>
                      <td className="text-sm">{alocacao.observacoes || '-'}</td>
                      <td>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(alocacao)}
                            className="btn btn-sm btn-secondary"
                            title="Editar"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(alocacao)}
                            className="btn btn-sm btn-danger"
                            title="Excluir"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {modal.isOpen && (
        <div className="modal-overlay" onClick={modal.close}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{modal.data.title}</h2>
              <button onClick={modal.close} className="modal-close">
                ×
              </button>
            </div>

            <form onSubmit={form.handleSubmit(handleSubmit)}>
              <div className="form-group">
                <label className="form-label">Evento *</label>
                <select
                  value={form.values.evento_id}
                  onChange={(e) => form.setValue('evento_id', e.target.value)}
                  onBlur={() => form.setTouched('evento_id')}
                  className={`form-select ${form.errors.evento_id && form.touched.evento_id ? 'border-red-500' : ''}`}
                >
                  <option value="">Selecione um evento</option>
                  {eventos.map((evento) => (
                    <option key={evento.id} value={evento.id}>
                      {evento.nome}
                    </option>
                  ))}
                </select>
                {form.errors.evento_id && form.touched.evento_id && (
                  <div className="form-error">{form.errors.evento_id}</div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Quarto *</label>
                  <select
                    value={form.values.quarto_id}
                    onChange={(e) => form.setValue('quarto_id', e.target.value)}
                    onBlur={() => form.setTouched('quarto_id')}
                    className={`form-select ${form.errors.quarto_id && form.touched.quarto_id ? 'border-red-500' : ''}`}
                  >
                    <option value="">Selecione um quarto</option>
                    {quartos.map((quarto) => {
                      const local = locais.find(l => l.id === quarto.local_id);
                      return (
                        <option key={quarto.id} value={quarto.id}>
                          {quarto.numero || quarto.nome} - {local?.nome || 'N/A'} (Cap: {quarto.capacidade}, {quarto.genero})
                        </option>
                      );
                    })}
                  </select>
                  {form.errors.quarto_id && form.touched.quarto_id && (
                    <div className="form-error">{form.errors.quarto_id}</div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Caravana *</label>
                  <select
                    value={form.values.caravana_id}
                    onChange={(e) => form.setValue('caravana_id', e.target.value)}
                    onBlur={() => form.setTouched('caravana_id')}
                    className={`form-select ${form.errors.caravana_id && form.touched.caravana_id ? 'border-red-500' : ''}`}
                  >
                    <option value="">Selecione uma caravana</option>
                    {caravanas
                      .filter(c => {
                        // Se nenhum evento selecionado, mostra todas as caravanas
                        if (!form.values.evento_id) return true;
                        // Se evento selecionado, filtra por evento_id
                        return c.evento_id?.toString() === form.values.evento_id;
                      })
                      .map((caravana) => {
                        const igreja = igrejas.find(i => i.id === caravana.igreja_id);
                        const total = (caravana.qtd_homens || 0) + (caravana.qtd_mulheres || 0);
                        return (
                          <option key={caravana.id} value={caravana.id}>
                            {caravana.nome} - {igreja?.nome || caravana.Igreja?.nome || 'N/A'} ({total} pessoas)
                          </option>
                        );
                      })}
                  </select>
                  {form.errors.caravana_id && form.touched.caravana_id && (
                    <div className="form-error">{form.errors.caravana_id}</div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Quantidade *</label>
                <input
                  type="number"
                  value={form.values.quantidade}
                  onChange={(e) => form.setValue('quantidade', e.target.value)}
                  onBlur={() => form.setTouched('quantidade')}
                  className={`form-input ${form.errors.quantidade && form.touched.quantidade ? 'border-red-500' : ''}`}
                  placeholder="Digite a quantidade de pessoas"
                  min="1"
                />
                {form.errors.quantidade && form.touched.quantidade && (
                  <div className="form-error">{form.errors.quantidade}</div>
                )}
                
                {/* Exibir informações do quarto selecionado */}
                {form.values.quarto_id && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                    <div className="text-sm text-blue-700">
                      <strong>Tipo de quarto:</strong> {quartos.find(q => q.id.toString() === form.values.quarto_id)?.genero || 'N/A'}
                      <br />
                      <span className="text-xs text-blue-600">
                        {(() => {
                          const genero = quartos.find(q => q.id.toString() === form.values.quarto_id)?.genero?.toLowerCase();
                          if (genero === 'masculino' || genero === 'homem') {
                            return 'Esta quantidade será validada contra os homens da caravana';
                          } else if (genero === 'feminino' || genero === 'mulher') {
                            return 'Esta quantidade será validada contra as mulheres da caravana';
                          } else {
                            return 'Esta quantidade será validada contra o total da caravana (homens + mulheres)';
                          }
                        })()}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Observações</label>
                <textarea
                  value={form.values.observacoes}
                  onChange={(e) => form.setValue('observacoes', e.target.value)}
                  className="form-input"
                  placeholder="Digite observações adicionais"
                  rows="3"
                />
              </div>

              <div className="modal-footer">
                <button type="button" onClick={modal.close} className="btn btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  {modal.data.mode === 'create' ? 'Criar' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Alocacoes;